// Session state for the whole app: loading → signedOut | signedIn.

import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from "react";
import * as auth from "./auth";

type AuthStatus = "loading" | "signedOut" | "signedIn";

interface Ctx {
  status: AuthStatus;
  signIn: (email: string, password: string) => Promise<auth.SignInResult>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Context = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    void (async () => {
      try {
        await auth.configureAuth();
        setStatus((await auth.hasSession()) ? "signedIn" : "signedOut");
      } catch {
        setStatus("signedOut");
      }
    })();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await auth.signInWithPassword(email, password);
    if (result === "done") setStatus("signedIn");
    return result;
  }, []);

  const completeNewPassword = useCallback(async (newPassword: string) => {
    await auth.completeNewPassword(newPassword);
    setStatus("signedIn");
  }, []);

  const signOut = useCallback(async () => {
    await auth.signOut();
    setStatus("signedOut");
  }, []);

  return (
    <Context.Provider value={{ status, signIn, completeNewPassword, signOut }}>
      {children}
    </Context.Provider>
  );
}

export function useAuth(): Ctx {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
